'use client';

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { reportReactBoundaryError } from '@/lib/clientMonitoring';

type Props = {
  children: ReactNode;
  /** Optional custom fallback (e.g. tests or embedded widgets). */
  fallback?: ReactNode;
};

type State = {
  hasError: boolean;
  error: Error | null;
};

function DefaultFallback({
  error,
  onReset,
}: {
  error: Error | null;
  onReset: () => void;
}) {
  return (
    <div
      className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-4 py-12 text-center"
      role="alert"
    >
      <h1 className="text-xl font-semibold text-foreground">Something went wrong</h1>
      <p className="max-w-md text-sm text-muted-foreground">
        This part of the app hit an unexpected error. You can try again. If the problem continues,
        contact support with the time of the error.
      </p>
      {process.env.NODE_ENV === 'development' && error?.message ? (
        <pre className="max-w-full overflow-x-auto rounded-md bg-muted p-3 text-left text-xs text-muted-foreground">
          {error.message}
        </pre>
      ) : null}
      <Button type="button" onClick={onReset} variant="default">
        Try again
      </Button>
    </div>
  );
}

/**
 * Catches render errors in child components and shows a recovery UI instead of a blank tree.
 * Does not replace route-level error handling for async server errors (see backlog R2).
 */
export default class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    reportReactBoundaryError(error, {
      componentStack: errorInfo.componentStack ?? undefined,
    });
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }
      return <DefaultFallback error={this.state.error} onReset={this.handleReset} />;
    }
    return this.props.children;
  }
}
